import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FaEnvelope, FaLock, FaEye, FaEyeSlash, FaSpinner } from 'react-icons/fa';
import axios from 'axios';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Header from '../components/Header';
import { useAuth } from '../context/AuthContext';

import { API_URL } from '../config/api';

const Login = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { login } = useAuth();
    const [showPassword, setShowPassword] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [loginData, setLoginData] = useState({ email: '', password: '' });

    const handleLoginChange = (e) => {
        setLoginData({ ...loginData, [e.target.name]: e.target.value });
    };

    const handleLoginSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);

        try {
            const response = await axios.post(`${API_URL}/login`, {
                email: loginData.email,
                password: loginData.password,
            });

            const { token, user, message } = response.data;

            // Utiliser le contexte pour gérer l'authentification
            login(user, token);

            // Toast de succès
            toast.success(message || 'Connexion réussie !', {
                position: 'top-right',
                autoClose: 2000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                style: {
                    borderRadius: '12px',
                    fontWeight: '600',
                },
            });

            // Redirection vers la page demandée ou dashboard
            const from = location.state?.from?.pathname || '/admin/dashboard';
            setTimeout(() => {
                navigate(from, { replace: true });
            }, 1500);

        } catch (error) {
            const errorMessage =
                error.response?.data?.message || 'Une erreur est survenue. Veuillez réessayer.';

            toast.error(errorMessage, {
                position: 'top-right',
                autoClose: 4000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                style: {
                    borderRadius: '12px',
                    fontWeight: '600',
                },
            });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <>
            <Header />
            <ToastContainer />
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');
                
                * {
                    margin: 0;
                    padding: 0;
                    box-sizing: border-box;
                }
                
                body {
                    margin: 0;
                    padding: 0;
                    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
                }
                
                .admin-login-page {
                    min-height: 100vh;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    padding: 120px 20px 60px;
                    background: linear-gradient(135deg, #f5f7fa 0%, #e8f5e9 100%);
                    position: relative;
                    overflow: hidden;
                }
                
                .admin-login-page::before {
                    content: '';
                    position: absolute;
                    top: -100px;
                    right: -100px;
                    width: 400px;
                    height: 400px;
                    background: radial-gradient(circle, rgba(25, 135, 84, 0.1) 0%, transparent 70%);
                    border-radius: 50%;
                    z-index: 0;
                }
                
                .admin-login-page::after {
                    content: '';
                    position: absolute;
                    bottom: -150px;
                    left: -150px;
                    width: 500px;
                    height: 500px;
                    background: radial-gradient(circle, rgba(32, 201, 151, 0.08) 0%, transparent 70%);
                    border-radius: 50%;
                    z-index: 0;
                }
                
                .admin-login-container {
                    max-width: 480px;
                    width: 100%;
                    background: white;
                    border-radius: 16px;
                    overflow: hidden;
                    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.08);
                    position: relative;
                    z-index: 1;
                    animation: slideUp 0.6s ease-out;
                }
                
                @keyframes slideUp {
                    from {
                        opacity: 0;
                        transform: translateY(30px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }
                
                .admin-form-section {
                    padding: 50px 45px;
                    display: flex;
                    flex-direction: column;
                    justify-content: center;
                }
                
                .admin-brand {
                    margin-bottom: 36px;
                }
                
                .admin-brand-text {
                    font-size: 18px;
                    font-weight: 700;
                    color: #2d3748;
                    letter-spacing: 0.3px;
                }
                
                .admin-header {
                    margin-bottom: 32px;
                }
                
                .admin-title {
                    font-size: 30px;
                    font-weight: 700;
                    color: #1a202c;
                    margin-bottom: 10px;
                    letter-spacing: -0.5px;
                }
                
                .admin-subtitle {
                    font-size: 15px;
                    color: #718096;
                    font-weight: 400;
                    line-height: 1.5;
                }
                
                .admin-form {
                    display: flex;
                    flex-direction: column;
                    gap: 22px;
                }
                
                .form-field {
                    display: flex;
                    flex-direction: column;
                    gap: 10px;
                }
                
                .field-label {
                    font-size: 14px;
                    font-weight: 600;
                    color: #2d3748;
                    letter-spacing: 0.3px;
                }
                
                .input-container {
                    position: relative;
                }
                
                .icon-left {
                    position: absolute;
                    left: 18px;
                    top: 50%;
                    transform: translateY(-50%);
                    color: #a0aec0;
                    font-size: 18px;
                    pointer-events: none;
                    transition: color 0.3s ease;
                }
                
                .admin-input {
                    width: 100%;
                    padding: 16px 20px 16px 52px;
                    border: 2px solid #e2e8f0;
                    border-radius: 12px;
                    font-size: 15px;
                    font-family: inherit;
                    background: #fafafa;
                    transition: all 0.3s ease;
                    font-weight: 400;
                }
                
                .admin-input:focus {
                    outline: none;
                    border-color: #198754;
                    background: white;
                    box-shadow: 0 0 0 4px rgba(25, 135, 84, 0.1);
                }
                
                .admin-input:focus ~ .icon-left {
                    color: #198754;
                }

                .admin-input:disabled {
                    opacity: 0.6;
                    cursor: not-allowed;
                }
                
                .icon-right {
                    position: absolute;
                    right: 18px;
                    top: 50%;
                    transform: translateY(-50%);
                    background: none;
                    border: none;
                    color: #a0aec0;
                    cursor: pointer;
                    font-size: 18px;
                    padding: 6px;
                    transition: color 0.3s ease;
                }
                
                .icon-right:hover {
                    color: #198754;
                }
                
                .form-footer {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-top: -8px;
                }
                
                .remember-checkbox {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    cursor: pointer;
                }
                
                .remember-checkbox input {
                    width: 20px;
                    height: 20px;
                    cursor: pointer;
                    accent-color: #198754;
                }
                
                .remember-checkbox span {
                    font-size: 14px;
                    color: #4a5568;
                    font-weight: 500;
                }
                
                .forgot-link {
                    font-size: 14px;
                    color: #198754;
                    text-decoration: none;
                    font-weight: 600;
                    transition: color 0.3s ease;
                }
                
                .forgot-link:hover {
                    color: #0d6938;
                }
                
                .login-button {
                    width: 100%;
                    padding: 15px;
                    background: linear-gradient(135deg, #198754 0%, #0d6938 100%);
                    color: white;
                    border: none;
                    border-radius: 12px;
                    font-size: 15px;
                    font-weight: 600;
                    cursor: pointer;
                    font-family: inherit;
                    box-shadow: 0 8px 20px rgba(25, 135, 84, 0.25);
                    transition: all 0.3s ease;
                    letter-spacing: 0.5px;
                    margin-top: 10px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 10px;
                }
                
                .login-button:hover:not(:disabled) {
                    transform: translateY(-2px);
                    box-shadow: 0 15px 40px rgba(25, 135, 84, 0.4);
                }
                
                .login-button:active:not(:disabled) {
                    transform: translateY(0);
                }

                .login-button:disabled {
                    opacity: 0.75;
                    cursor: not-allowed;
                    transform: none;
                }

                .spinner {
                    animation: spin 0.8s linear infinite;
                }

                @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }
                
                @media (max-width: 968px) {
                    .admin-login-container {
                        max-width: 480px;
                    }
                    .admin-form-section {
                        padding: 45px 35px;
                    }
                }
                
                @media (max-width: 640px) {
                    .admin-login-page {
                        padding: 80px 16px 16px;
                    }
                    .admin-form-section {
                        padding: 40px 28px;
                    }
                    .admin-title {
                        font-size: 30px;
                    }
                    .form-footer {
                        flex-direction: column;
                        gap: 16px;
                        align-items: flex-start;
                    }
                }
            `}</style>

            <div className="admin-login-page">
                <div className="admin-login-container">
                    <div className="admin-form-section">
                        <div className="admin-brand">
                            <div className="admin-brand-text">CINEF Admin</div>
                        </div>

                        <div className="admin-header">
                            <h1 className="admin-title">Connexion Admin</h1>
                            <p className="admin-subtitle">
                                Accédez au tableau de bord de gestion
                            </p>
                        </div>

                        <form className="admin-form" onSubmit={handleLoginSubmit}>
                            <div className="form-field">
                                <label className="field-label">Adresse Email</label>
                                <div className="input-container">
                                    <input
                                        type="email"
                                        name="email"
                                        className="admin-input"
                                        placeholder="admin@cinef.com"
                                        value={loginData.email}
                                        onChange={handleLoginChange}
                                        disabled={isLoading}
                                        required
                                    />
                                    <FaEnvelope className="icon-left" />
                                </div>
                            </div>

                            <div className="form-field">
                                <label className="field-label">Mot de passe</label>
                                <div className="input-container">
                                    <input
                                        type={showPassword ? 'text' : 'password'}
                                        name="password"
                                        className="admin-input"
                                        placeholder="Entrez votre mot de passe"
                                        value={loginData.password}
                                        onChange={handleLoginChange}
                                        disabled={isLoading}
                                        required
                                    />
                                    <FaLock className="icon-left" />
                                    <button
                                        type="button"
                                        className="icon-right"
                                        onClick={() => setShowPassword(!showPassword)}
                                        disabled={isLoading}
                                    >
                                        {showPassword ? <FaEyeSlash /> : <FaEye />}
                                    </button>
                                </div>
                            </div>

                            <div className="form-footer">
                                <label className="remember-checkbox">
                                    <input type="checkbox" />
                                    <span>Se souvenir de moi</span>
                                </label>
                                <a href="#" className="forgot-link">
                                    Mot de passe oublié ?
                                </a>
                            </div>

                            <button
                                type="submit"
                                className="login-button"
                                disabled={isLoading}
                            >
                                {isLoading ? (
                                    <>
                                        <FaSpinner className="spinner" />
                                        <span>Connexion en cours...</span>
                                    </>
                                ) : (
                                    'CONNEXION'
                                )}
                            </button>
                        </form>
                    </div>
                </div>
            </div>
        </>
    );
};

export default Login;